import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Column, ConfirmationModal, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import BankSecurityRow from '~/components/BankSecurityRow/BankSecurityRow';
import { AddPaymentMethodOptions } from '~/components/ManagePaymentMethods/AddPaymentMethodOptions/AddPaymentMethodOptions';
import { ConnectToAgreementsModal } from '~/components/ManagePaymentMethods/ConnectToAgreementsModal/ConnectToAgreementsModal';
import { useAddPaymentMethodFlow } from '~/components/ManagePaymentMethods/hooks/useAddPaymentMethodFlow';
import { PartialConnectError, useConnectFundingSourceToAgreements } from '~/components/ManagePaymentMethods/hooks/useConnectFundingSourceToAgreements';
import { useStore } from '~/contexts';
import { QueryKeys } from '~/hooks/reactQuery/queryKeys';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
import { useClientAgreementsForConnect } from '../hooks/useClientAgreementsForConnect';
import { usePaymentMethods } from '../hooks/usePaymentMethods';
import { clientPaymentMethodsTextsPath, connectFailureText, skipConnectToastText } from './ClientDetailsPaymentMethods.utils';
export const GetClientDetailsPaymentMethodsTID = (parentTestId)=>getTestIds(parentTestId);
export function ClientDetailsPaymentMethods(param) {
    let { clientId, dismissModal, testId } = param;
    var _useStore;
    const { ClientDetailsPaymentMethodsTestIds } = useMemo(()=>GetClientDetailsPaymentMethodsTID(testId), [
        testId
    ]);
    const { t: texts } = useLocaleKeys(clientPaymentMethodsTextsPath);
    const { t: managePaymentMethodsTexts } = useLocaleKeys('payment.managePaymentMethods');
    const queryClient = useQueryClient();
    const overlayContext = useOverlayContext();
    const vendorId = (_useStore = useStore(selectors.user.businessId)) !== null && _useStore !== void 0 ? _useStore : '';
    const apiGatewayHost = useStore((state)=>state.session.apiGatewayHost);
    const [step, setStep] = useState('add');
    const [createdFundingSourceId, setCreatedFundingSourceId] = useState(null);
    const { agreements, isLoading, isError, hasLoaded } = useClientAgreementsForConnect(clientId, {
        refreshOnHookMount: true
    });
    const { paymentMethods, refresh } = usePaymentMethods(clientId, {
        refreshOnHookMount: true
    });
    const refreshPaymentMethods = useCallback(()=>refresh({
            throwOnError: false
        }).then((result)=>{
            var _result_data;
            return {
                paymentMethods: (_result_data = result.data) !== null && _result_data !== void 0 ? _result_data : []
            };
        }), [
        refresh
    ]);
    const invalidatePaymentMethods = useCallback(()=>queryClient.invalidateQueries({
            queryKey: [
                QueryKeys.CustomerPaymentMethods,
                vendorId,
                clientId
            ]
        }), [
        queryClient,
        vendorId,
        clientId
    ]);
    const { connect, isLoading: isConnecting } = useConnectFundingSourceToAgreements({
        agreements,
        onSuccess: (connectedAgreementCount)=>{
            invalidatePaymentMethods();
            overlayContext.showSuccessToast(connectedAgreementCount > 0 ? texts.connectSuccess() : texts.addSuccess(), ClientDetailsPaymentMethodsTestIds.SuccessToast());
            dismissModal();
        }
    });
    const completeWithToast = useCallback((message)=>{
        invalidatePaymentMethods();
        overlayContext.showSuccessToast(message, ClientDetailsPaymentMethodsTestIds.SuccessToast());
        dismissModal();
    }, [
        invalidatePaymentMethods,
        ClientDetailsPaymentMethodsTestIds,
        dismissModal
    ]);
    const completeWithoutConnecting = useCallback(()=>completeWithToast(texts.addSuccess()), [
        completeWithToast
    ]);
    const hasNoAgreementsToConnect = hasLoaded && agreements.length === 0;
    const onFundingSourceCreated = useCallback(async (fundingSourceId)=>{
        const { paymentMethods: refreshed } = await refreshPaymentMethods();
        const created = refreshed.find((paymentMethod)=>paymentMethod.id === fundingSourceId);
        const skipToast = skipConnectToastText({
            hasNoAgreementsToConnect,
            createdPaymentMethod: created,
            texts
        });
        if (skipToast) {
            completeWithToast(skipToast);
            return;
        }
        setCreatedFundingSourceId(fundingSourceId);
        setStep('connect');
    }, [
        refreshPaymentMethods,
        hasNoAgreementsToConnect,
        completeWithToast
    ]);
    const flow = useAddPaymentMethodFlow({
        mode: 'modal',
        vendorId,
        customerId: clientId,
        paymentMethods,
        refresh: refreshPaymentMethods,
        onFundingSourceCreated
    });
    const onSave = (selectedAgreementIds)=>connect({
            fundingSourceId: createdFundingSourceId !== null && createdFundingSourceId !== void 0 ? createdFundingSourceId : '',
            selectedAgreementIds
        }).catch((error)=>{
            if (error instanceof PartialConnectError) {
                overlayContext.showErrorToast(connectFailureText(error, texts), ClientDetailsPaymentMethodsTestIds.ErrorToast());
            }
        });
    const createdPaymentMethod = paymentMethods.find((paymentMethod)=>paymentMethod.id === createdFundingSourceId);
    const isConnectStep = step === 'connect';
    // WHY: dismissModal is rebuilt on every ModalContainer render, so this effect's deps churn while its condition holds.
    const hasCompletedWithoutConnecting = useRef(false);
    useEffect(()=>{
        if (isConnectStep && hasNoAgreementsToConnect && !hasCompletedWithoutConnecting.current) {
            hasCompletedWithoutConnecting.current = true;
            completeWithoutConnecting();
        }
    }, [
        isConnectStep,
        hasNoAgreementsToConnect,
        completeWithoutConnecting
    ]);
    const addStepContent = /*#__PURE__*/ _jsxs(Column, {
        gap: 24,
        children: [
            /*#__PURE__*/ _jsx(AddPaymentMethodOptions, {
                testId: ClientDetailsPaymentMethodsTestIds.Root(),
                flow: flow,
                vendorId: vendorId,
                customerId: clientId,
                creditCardFeeLabel: managePaymentMethodsTexts.creditCardFeeByClient(),
                achFeeLabel: managePaymentMethodsTexts.noFee()
            }),
            /*#__PURE__*/ _jsx(BankSecurityRow, {
                "data-testid": ClientDetailsPaymentMethodsTestIds.BankSecurity()
            })
        ]
    });
    if (isConnectStep) {
        return /*#__PURE__*/ _jsx(ConnectToAgreementsModal, {
            paymentMethod: createdPaymentMethod,
            vendorId: vendorId,
            customerId: clientId,
            apiGatewayHost: apiGatewayHost,
            subtitle: texts.connectStepSubtitle(),
            agreements: agreements,
            isLoadingAgreements: isLoading,
            isErrorAgreements: isError,
            isSaving: isConnecting,
            onSave: onSave,
            onSkip: completeWithoutConnecting,
            onClose: dismissModal,
            testId: ClientDetailsPaymentMethodsTestIds.Root()
        });
    }
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        testId: ClientDetailsPaymentMethodsTestIds.Root(),
        useStandardTextColors: true,
        openBottomSheetOnMobile: true,
        textAlignCenter: false,
        width: "760px",
        title: texts.modalTitle(),
        onCloseButtonClick: dismissModal,
        bi: {
            closeButton: {
                name: 'client_detail_add_payment_method_close'
            },
            primaryButton: {
                name: 'connect_payment_method_save'
            },
            secondaryButton: {
                name: 'skip_connect_agreements'
            }
        },
        content: addStepContent
    });
}
